import { useState } from 'react'

import {
  FinanceFactSummariesDocument,
  FinanceFactSummariesQuery,
  FinanceFactSummariesQueryVariables,
  FinancialWorkspaceValueObjectsFinanceTypeEnum,
  FinancialWorkspaceValueObjectsInsightTableGroupEnum,
  FinancialWorkspaceValueObjectsPeriodEnum,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useLazyQuery } from 'modules/core/hooks/useQuery'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import {
  InsightTableGroupBy,
  InsightTablePeriod,
  LazyChildren,
  PaginatedTableItemPriority,
} from 'modules/finance/components/FinancialWorkspace/types/table'
import { useCustomFilters } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/CustomFilters'
import {
  Variables,
  getFilteredVariables,
} from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/lib/filteredVariables'
import { FinancialWorkspaceResource } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/types/data'

export type FinanceFactSummary =
  | FinanceFactSummariesQuery['financialWorkspace']['financeFactSummariesConnection']['edges'][number]['node']
  | undefined

interface FinanceSummariesVariables {
  search?: string
  after?: string
  itemsPerPage?: number
}

interface Props {
  skip?: boolean
  compareTo?: boolean
  customCategoryIds?: number[]
  resource?: FinancialWorkspaceResource
  variables?: FinanceSummariesVariables
  financeTypes: FinancialWorkspaceValueObjectsFinanceTypeEnum[]
  groupBy?: FinancialWorkspaceValueObjectsInsightTableGroupEnum
}

interface FormattedData {
  id: string
  label: string
  grey?: boolean
  tooltip?: string
  italic?: boolean
  dashed?: boolean
  subLabel?: string
  loading?: boolean
  data: FinanceFactSummary
  onLazyChildren?: LazyChildren
  comparisonData?: FinanceFactSummary
  priority?: PaginatedTableItemPriority
  titleWrapper?: React.FC<{ children: React.ReactNode }>
}

export const DEFAULT_PERIOD: FinancialWorkspaceValueObjectsPeriodEnum = InsightTablePeriod.Monthly

export const formatData = ({
  id,
  data,
  grey,
  label,
  italic,
  dashed,
  tooltip,
  loading,
  subLabel,
  priority,
  titleWrapper,
  comparisonData,
  onLazyChildren,
}: FormattedData) => {
  return [
    {
      id,
      grey,
      italic,
      dashed,
      loading,
      tooltip,
      priority,
      subLabel,
      headers: [],
      onLazyChildren,
      noTotal: false,
      category: null,
      categoryLabel: null,
      label: data?.label ?? label,
      titleWrapper,
      data:
        data?.rows.map((item) => ({
          noTotal: false,
          period: DEFAULT_PERIOD,
          label: item.columnLabel,
          range: item.columnLabel,
          totalAmountInCents: item.amountInCents,
          totalAmountWithPrecision: item.amountWithPrecision ?? undefined,
        })) ?? [],
      comparisonData:
        comparisonData?.rows.map((item) => ({
          noTotal: false,
          period: DEFAULT_PERIOD,
          label: item.columnLabel,
          range: item.columnLabel,
          totalAmountInCents: item.amountInCents,
          totalAmountWithPrecision: item.amountWithPrecision ?? undefined,
        })) ?? [],
      children: (data?.children || []).map((child, index) => {
        const comparisonDataChild = comparisonData?.children?.[index]
        return {
          id: child.label,
          label: child.label,
          data: child.rows?.map((row) => ({
            period: DEFAULT_PERIOD,
            label: row.columnLabel,
            range: row.columnLabel,
            totalAmountInCents: row.amountInCents,
            totalAmountWithPrecision: row.amountWithPrecision ?? undefined,
          })),
          comparisonData: comparisonDataChild?.rows?.map((row) => ({
            period: DEFAULT_PERIOD,
            label: row.columnLabel,
            range: row.columnLabel,
            totalAmountInCents: row.amountInCents,
            totalAmountWithPrecision: row.amountWithPrecision ?? undefined,
          })),
        }
      }),
    },
  ]
}

export const useFinanceSummaries = ({
  skip,
  resource,
  compareTo,
  variables,
  financeTypes,
  groupBy = InsightTableGroupBy.Total,
}: Props) => {
  const { formattedState } = useFinancialWorkspace()
  const { formattedState: customFilters } = useCustomFilters()
  const {
    formattedEndDate,
    formattedStartDate,
    formattedCompareToEndDate,
    formattedCompareToStartDate,
  } = formattedState
  const [refetching, setRefetching] = useState(false)

  const customCategoryIds = useUnsafeMemo(
    () =>
      compact(
        financeTypes.includes('expense')
          ? customFilters?.employeeCosts?.employee_expenses.map((i) => Number(i))
          : []
      ).filter(Boolean),
    [customFilters, financeTypes]
  )

  const agreementDataTypes = useUnsafeMemo(() => {
    return compact(
      financeTypes.includes('agreement') ? customFilters?.employeeCosts?.agreement_data : []
    ).filter(Boolean)
  }, [customFilters, financeTypes])

  const filterVariables: Variables = useUnsafeMemo(() => {
    return {
      groupBy,
      financeTypes,
      search: variables?.search,
      formattedEndDate: compareTo ? formattedCompareToEndDate : formattedEndDate,
      formattedStartDate: compareTo ? formattedCompareToStartDate : formattedStartDate,
      ...getFilteredVariables({ formattedState, customCategoryIds, resource, agreementDataTypes }),
    }
  }, [
    groupBy,
    resource,
    compareTo,
    financeTypes,
    formattedState,
    formattedEndDate,
    customCategoryIds,
    variables?.search,
    formattedStartDate,
    agreementDataTypes,
    formattedCompareToEndDate,
    formattedCompareToStartDate,
  ])

  const [fetchFinanceSummaries, { data, loading, refetch }] = useLazyQuery(
    FinanceFactSummariesDocument,
    {
      variables: {
        costType: 'labor_cost',
        ...filterVariables,
        ...variables,
      },
      skip,
      fetchPolicy: 'no-cache',
    }
  )

  const dataConnection = data?.financialWorkspace.financeFactSummariesConnection

  const items = dataConnection?.edges.map((edge) => edge.node)

  const handleRefetch = useUnsafeCallback(async () => {
    setRefetching(true)
    await refetch()
    setRefetching(false)
  }, [refetch])

  return {
    data: items,
    fetchFinanceSummaries,
    refetch: handleRefetch,
    loading: loading || refetching,
    pagination: {
      hasNextPage: dataConnection?.pageInfo.hasNextPage ?? false,
      endCursor: dataConnection?.pageInfo.endCursor ?? undefined,
      totalCount: dataConnection?.totalCount ?? 0,
    },
    filterVariables,
  }
}
